import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { updateCountryPreference } from '../../redux/actions/userAction';
import ReactGA from 'react-ga4';
import { tracker } from '../../analytics';
import FlagEmoji from './FlagEmoji';

const CountryDropdown = ({
  size = "medium",
  minWidth = { xs: 200, md: 280 },
  showLabel = true,
  label = null,
  disabled = false,
  sx = {},
  // Controlled mode: when `value` + `onChange` are provided, the dropdown
  // reflects that external value (e.g. a contract's country) and delegates
  // persistence to the caller instead of writing to the user's global
  // countryPreference. The success snackbar is still shown here.
  value = undefined,
  onChange = undefined,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const isControlled = value !== undefined && typeof onChange === 'function';

  const countryPref = useSelector(state => state.user?.countryPreference);
  // CountryDropdown is single-select; use first country from array
  const countryPreference = Array.isArray(countryPref) ? (countryPref[0] || 'MA') : (countryPref || 'MA');
  const [selectedCountry, setSelectedCountry] = useState(isControlled ? (value || 'MA') : 'MA');
  const [isSavingCountry, setIsSavingCountry] = useState(false);

  // Update local state when the bound source changes
  useEffect(() => {
    if (isControlled) {
      setSelectedCountry(value || 'MA');
    } else if (countryPreference) {
      setSelectedCountry(countryPreference || 'MA');
    }
  }, [isControlled, value, countryPreference]);

  const handleCountryChange = async (event) => {
    const newCountry = event.target.value;
    const previousCountry = selectedCountry;
    setSelectedCountry(newCountry);

    setIsSavingCountry(true);
    try {
      if (isControlled) {
        await onChange(newCountry, previousCountry);
      } else {
        await dispatch(updateCountryPreference(newCountry));
      }

      const countryNames = {
        MA: t("country_maroc"),
        AE: t("country_uae"),
      };
      enqueueSnackbar(
        t("country_changed_message", {
          country: countryNames[newCountry] || t("country_unknown"),
        }),
        {
          variant: "info",
        }
      );

      // Google Analytics event for country change
      ReactGA.event({
        category: "Country",
        action: "Country Changed",
        label: `New Country: ${newCountry}`,
      });
      tracker.event({
        category: "Country",
        action: "Country Changed",
        label: `New Country: ${newCountry}`,
      });
    } catch (error) {
      console.error("Failed to update country:", error);
      enqueueSnackbar(t("error_saving"), {
        variant: "error",
      });
      // Revert the change if it failed
      setSelectedCountry(previousCountry);
    }
    setIsSavingCountry(false);
  };

  const defaultSx = {
    minWidth,
    mt: { xs: 0.5, md: 0 },
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      backgroundColor: theme.palette.background.paper,
      height: { xs: '36px', md: '48px' },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.divider
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main'
      }
    },
    '& .MuiInputLabel-root': {
      fontSize: { xs: '0.875rem', md: '1rem' },
      '&.Mui-focused': {
        color: 'primary.main'
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
      <FormControl 
        size={size} 
        variant="outlined"
        disabled={disabled}
        sx={{ ...defaultSx, ...sx }}
      >
        {showLabel && (
          <InputLabel 
            id="country-select-label"
            sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}
          >
            {label || t("search_country")}
          </InputLabel>
        )}
        <Select
          labelId="country-select-label"
          value={selectedCountry}
          label={showLabel ? (label || t("search_country")) : undefined}
          onChange={handleCountryChange}
          disabled={disabled || isSavingCountry}
          sx={{
            fontSize: { xs: '1rem', md: '1.1rem' },
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              paddingRight: '32px !important'
            }
          }}
        >
          <MenuItem value="MA" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlagEmoji emoji="🇲🇦" /> {t("country_maroc")}
          </MenuItem>
          <MenuItem value="AE" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FlagEmoji emoji="🇦🇪" /> {t("country_uae")}
          </MenuItem>
        </Select>
        {isSavingCountry && (
          <CircularProgress 
            size={16} 
            sx={{ 
              position: 'absolute', 
              right: 8, 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: 'primary.main'
            }} 
          />
        )}
      </FormControl>
    </Box>
  );
};

export default CountryDropdown; 